import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const clearWagmiStorage = () => {
  try {
    // Clear all wagmi-related storage keys
    const keysToRemove = [
      'wagmi',
      'wagmi.store',
      'wagmi.connected',
      'wagmi.wallet',
      'wagmi.recentConnectorId',
      'wagmi.injected.connected',
      'wagmi.metaMask.connected',
      'rk-recent',
    ];
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Also clear any keys that start with 'wagmi.' or 'rk-'
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('wagmi.') || key.startsWith('rk-')) {
        localStorage.removeItem(key);
      }
    });
  } catch {
    // Ignore storage errors
  }
};

// Calculate ASETQU amount from priceIdr using real-time market price if available
export const calculateAsetquAmount = (
  priceIdr: string | null,
  marketPrice: number
): { amount: string; isSmall: boolean } => {
  if (!priceIdr || !marketPrice || marketPrice === 0) {
    return { amount: '0', isSmall: false };
  }

  const priceIdrNum = parseFloat(priceIdr);
  const asetquAmount = priceIdrNum / marketPrice;

  // For extremely small amounts (less than 0.000001), use scientific notation
  if (asetquAmount < 0.000001 && asetquAmount > 0) {
    return {
      amount: asetquAmount.toExponential(2), // e.g., 1.20e-7
      isSmall: true,
    };
  }

  // Use more decimal places for small amounts, fewer for large amounts
  const decimals = asetquAmount < 0.001 ? 8 : asetquAmount < 1 ? 6 : asetquAmount < 100 ? 4 : 2;
  return {
    amount: asetquAmount.toLocaleString('id-ID', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }),
    isSmall: asetquAmount < 1,
  };
};

/**
 * Convert IDR to ASETQU token amount
 * @param idrAmount - Amount in IDR
 * @param pricePerToken - Current ASETQU price in IDR (from Indodax)
 * @returns ASETQU token amount (raw number)
 */
export const convertIdrToAsetqu = (idrAmount: number, pricePerToken: number): number => {
  if (pricePerToken === 0) return 0;
  return idrAmount / pricePerToken;
};

/**
 * Convert ASETQU token to IDR amount
 * @param tokenAmount - Amount in ASETQU tokens
 * @param pricePerToken - Current ASETQU price in IDR (from Indodax)
 * @returns IDR amount (raw number)
 */
export const convertAsetquToIdr = (tokenAmount: number, pricePerToken: number): number => {
  return tokenAmount * pricePerToken;
};

/**
 * Format ASETQU token amount with appropriate decimals
 * @param amount - Token amount
 * @returns Formatted string with appropriate decimal places
 */
export const formatAsetquAmount = (amount: number): string => {
  if (amount === 0) return '0';

  // For extremely small amounts (less than 0.000001), use scientific notation
  if (amount < 0.000001 && amount > 0) {
    return amount.toExponential(2);
  }

  // Use more decimal places for small amounts
  const decimals = amount < 0.001 ? 8 : amount < 1 ? 6 : amount < 100 ? 4 : 2;
  return amount.toLocaleString('id-ID', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format IDR amount with thousand separators
 * @param amount - IDR amount
 * @returns Formatted string with Rp prefix
 */
export const formatIdrAmount = (amount: number): string => {
  return `Rp ${amount.toLocaleString('id-ID', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

export function formatIdCompact(value: string, opts?: { maxFraction?: number }) {
  const maxFraction = opts?.maxFraction ?? 2;

  const abs = Math.abs(parseFloat(value));
  const sign = parseFloat(value) < 0 ? '-' : '';

  const format = (n: number) =>
    n
      .toLocaleString('id-ID', {
        maximumFractionDigits: maxFraction,
      })
      .replace(/,0+$/, '');

  if (abs < 1_000) return sign + format(abs);
  if (abs < 1_000_000) return sign + format(abs / 1_000) + ' Rb';
  if (abs < 1_000_000_000) return sign + format(abs / 1_000_000) + ' Jt';
  if (abs < 1_000_000_000_000) return sign + format(abs / 1_000_000_000) + ' M';
  return sign + format(abs / 1_000_000_000_000) + ' T';
}

export const getDisplayPointSource = (source: string) => {
  switch (source) {
    case 'DEPOSIT_AUCTION':
      return 'Deposit Auction';
    case 'REDEMPTION_CATALOG_DIRECT_SALE':
      return 'Proprimarket';
    case 'REDEMPTION_VOUCHER':
      return 'Proprivoucher';
    case 'REDEMPTION_BOX':
      return 'Mystery Box';
    case 'REFERRAL_COMMISSION':
      return 'Komisi Referral';
    case 'REDEMPTION':
      return 'Redeem';
    case 'OTHER':
      return 'Lainnya';
    // Deprecated sources
    case 'REVIEW_PROPERTY':
      return 'Review Properti';
    case 'REVIEW_CATALOG':
      return 'Review Katalog';
    case 'REVIEW_AUCTION':
      return 'Review Lelang';
    case 'CASHBACK_CATALOG':
      return 'Cashback Katalog';
    case 'CASHBACK_PROPERTY':
      return 'Cashback Properti';
    case 'CASHBACK_AUCTION':
      return 'Cashback Lelang';
    case 'ADMIN_ADJUSTMENT':
      return 'Penyesuaian Admin';
    default:
      return source;
  }
};

export const getReadableBlockchainError = (
  error: unknown,
  fallback = 'Transaksi gagal. Silakan coba lagi.'
): string => {
  if (!error) return fallback;

  const errorObject = error as {
    shortMessage?: string;
    message?: string;
    details?: string;
    cause?: { message?: string; shortMessage?: string; details?: string };
    data?: { message?: string };
  };

  const candidates = [
    errorObject.shortMessage,
    errorObject.message,
    errorObject.details,
    errorObject.data?.message,
    errorObject.cause?.shortMessage,
    errorObject.cause?.message,
    errorObject.cause?.details,
  ].filter(Boolean) as string[];

  const rawMessage = candidates.join(' | ').toLowerCase();

  if (!rawMessage) return fallback;

  const patterns: Array<{ regex: RegExp; message: string }> = [
    {
      regex: /user rejected|user denied|rejected transaction|rejected the request/i,
      message: 'Transaksi dibatalkan oleh pengguna.',
    },
    {
      regex: /insufficient funds|sender doesn\'t have enough funds|max upfront cost|balance is: 0/i,
      message: 'Saldo gas tidak cukup untuk memproses transaksi.',
    },
    {
      regex: /insufficient allowance|allowance|ERC20: insufficient allowance/i,
      message: 'Allowance token belum cukup. Silakan lakukan approval terlebih dahulu.',
    },
    {
      regex: /missing or invalid parameters|invalid parameters|invalid argument/i,
      message: 'Parameter transaksi tidak valid. Periksa kembali data transaksi.',
    },
    {
      regex: /nonce too low/i,
      message: 'Nonce transaksi terlalu rendah. Silakan coba lagi setelah beberapa detik.',
    },
    {
      regex: /replacement transaction underpriced/i,
      message: 'Transaksi sebelumnya masih pending. Tunggu atau naikkan gas.',
    },
    {
      regex: /execution reverted|revert/i,
      message: 'Transaksi gagal diproses oleh smart contract.',
    },
    {
      regex: /requested resource not available|rpc endpoint not found|rpc.*unavailable|resource not available/i,
      message: 'Jaringan blockchain sedang tidak tersedia. Silakan coba beberapa saat lagi.',
    },
    {
      regex: /network error|failed to fetch|timeout|ECONNREFUSED/i,
      message: 'Koneksi jaringan bermasalah. Silakan coba lagi.',
    },
  ];

  const matched = patterns.find(pattern => pattern.regex.test(rawMessage));
  if (matched) return matched.message;

  const revertMatch = rawMessage.match(/revert(?:ed)?(?::| with reason string)?\s*(.+)/i);
  if (revertMatch?.[1]) {
    return `Transaksi gagal: ${revertMatch[1].trim()}`;
  }

  return candidates[0] || fallback;
};
